import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { FaPlay, FaTimesCircle, FaPause, FaExpand } from "react-icons/fa";

const Gallery = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Video gallery items with all MP4 files
  const videoItems = [
    {
      id: "v1",
      category: "videos",
      title: "Custom Grillz Showcase 1",
      video: "/images/WhatsApp Video 2025-03-23 at 1.07.36 PM.mp4",
      type: "video",
    },
    {
      id: "v2",
      category: "videos",
      title: "Custom Grillz Showcase 2",
      video: "/images/WhatsApp Video 2025-03-23 at 1.08.17 PM.mp4",
      type: "video",
    },
    {
      id: "v3",
      category: "videos",
      title: "Craftsmanship Demo 1",
      video: "/images/WhatsApp Video 2025-03-23 at 12.52.30 PM.mp4",
      type: "video",
    },
    {
      id: "v4",
      category: "videos",
      title: "Craftsmanship Demo 2",
      video: "/images/WhatsApp Video 2025-03-23 at 12.55.38 PM.mp4",
      type: "video",
    },
    {
      id: "v5",
      category: "videos",
      title: "Craftsmanship Demo 3",
      video: "/images/WhatsApp Video 2025-03-23 at 12.55.41 PM.mp4",
      type: "video",
    },
    {
      id: "v6",
      category: "videos",
      title: "Latest Creation 1",
      video: "/images/WhatsApp Video 2026-01-02 at 12.16.01 PM.mp4",
      type: "video",
    },
    {
      id: "v7",
      category: "videos",
      title: "Latest Creation 2",
      video: "/images/WhatsApp Video 2026-01-02 at 12.16.08 PM.mp4",
      type: "video",
    },
    {
      id: "v8",
      category: "videos",
      title: "Latest Creation 3",
      video: "/images/WhatsApp Video 2026-01-02 at 12.16.22 PM.mp4",
      type: "video",
    },
    {
      id: "v9",
      category: "videos",
      title: "Latest Creation 4",
      video: "/images/WhatsApp Video 2026-01-02 at 12.16.27 PM.mp4",
      type: "video",
    },
    {
      id: "v10",
      category: "videos",
      title: "Latest Creation 5",
      video: "/images/WhatsApp Video 2026-01-02 at 12.16.35 PM.mp4",
      type: "video",
    },
    {
      id: "v11",
      category: "videos",
      title: "Latest Creation 6",
      video: "/images/WhatsApp Video 2026-01-02 at 12.16.44 PM.mp4",
      type: "video",
    },
    {
      id: "v12",
      category: "videos",
      title: "Latest Creation 7",
      video: "/images/WhatsApp Video 2026-01-02 at 12.17.04 PM.mp4",
      type: "video",
    },
    {
      id: "v13",
      category: "videos",
      title: "Latest Creation 8",
      video: "/images/WhatsApp Video 2026-01-02 at 12.17.09 PM.mp4",
      type: "video",
    },
  ];

  // Image gallery items
  const imageItems = [
    {
      id: 1,
      category: "grillz",
      title: "Diamond Fangs Grillz",
      image: "/images/bg-pic.jpg",
      type: "image",
    },
    {
      id: 2,
      category: "jewelry",
      title: "Cuban Link Chain",
      image: "/images/img2.jpg",
      type: "image",
    },
    {
      id: 3,
      category: "grillz",
      title: "Gold Bottom Grillz",
      image: "/images/img3.jpg",
      type: "image",
    },
    {
      id: 4,
      category: "jewelry",
      title: "Diamond Pendant",
      image: "/images/img1.jpg",
      type: "image",
    },
    {
      id: 5,
      category: "grillz",
      title: "Two-Tone Grillz",
      image: "/images/bg-pic.jpg",
      type: "image",
    },
    {
      id: 6,
      category: "jewelry",
      title: "Tennis Bracelet",
      image: "/images/img2.jpg",
      type: "image",
    },
  ];

  // Combine all items
  const allItems = [...imageItems, ...videoItems];

  // State for filtering and modal
  const [filter, setFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filtered gallery items based on selected category
  const filteredItems =
    filter === "all"
      ? allItems
      : allItems.filter((item) => item.category === filter);

  // Open modal with selected item
  const openModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setIsPlaying(false);
    document.body.style.overflow = "hidden";
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
    document.body.style.overflow = "auto";
  };

  // Toggle video play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Fullscreen video
  const goFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      }
    }
  };

  // Animation variants
  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <GalleryContainer>
      <PageHeader>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Our Gallery
        </motion.h1>
        <motion.div
          className="underline"
          initial={{ width: 0 }}
          animate={{ width: "80px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Browse our collection of custom jewelry, grillz, and video showcases
        </motion.p>
      </PageHeader>

      <FilterContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <FilterButton
            active={filter === "all"}
            onClick={() => setFilter("all")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            All
          </FilterButton>
          <FilterButton
            active={filter === "videos"}
            onClick={() => setFilter("videos")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Videos
          </FilterButton>
          <FilterButton
            active={filter === "grillz"}
            onClick={() => setFilter("grillz")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Grillz
          </FilterButton>
          <FilterButton
            active={filter === "jewelry"}
            onClick={() => setFilter("jewelry")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Jewelry
          </FilterButton>
        </motion.div>
      </FilterContainer>

      <GalleryGrid variants={staggerContainer} initial="hidden" animate="show">
        {filteredItems.map((item) => (
          <GalleryItem
            key={item.id}
            variants={fadeInUp}
            onClick={() => openModal(item)}
            whileHover={{
              y: -10,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
            }}
          >
            {item.type === "video" ? (
              <>
                <VideoThumbnail>
                  <video src={item.video} muted preload="metadata" />
                  <PlayOverlay>
                    <FaPlay />
                  </PlayOverlay>
                </VideoThumbnail>
                <GalleryItemOverlay>
                  <GalleryItemTitle>{item.title}</GalleryItemTitle>
                  <GalleryItemCategory>Video</GalleryItemCategory>
                </GalleryItemOverlay>
              </>
            ) : (
              <>
                <GalleryImage src={item.image} alt={item.title} />
                <GalleryItemOverlay>
                  <GalleryItemTitle>{item.title}</GalleryItemTitle>
                  <GalleryItemCategory>{item.category}</GalleryItemCategory>
                </GalleryItemOverlay>
              </>
            )}
          </GalleryItem>
        ))}
      </GalleryGrid>

      {filteredItems.length === 0 && (
        <NoResults>
          <p>No items found in this category.</p>
        </NoResults>
      )}

      {/* Modal */}
      {isModalOpen && selectedItem && (
        <Modal>
          <ModalOverlay onClick={closeModal} />
          <ModalContent
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <ModalCloseButton onClick={closeModal}>
              <FaTimesCircle />
            </ModalCloseButton>

            {selectedItem.type === "video" ? (
              <VideoModalContent>
                <VideoWrapper>
                  <video
                    ref={videoRef}
                    src={selectedItem.video}
                    onClick={togglePlay}
                    onEnded={() => setIsPlaying(false)}
                  />
                  {!isPlaying && (
                    <BigPlayButton onClick={togglePlay}>
                      <FaPlay />
                    </BigPlayButton>
                  )}
                </VideoWrapper>
                <VideoControls>
                  <ControlButton onClick={togglePlay}>
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </ControlButton>
                  <VideoTitle>{selectedItem.title}</VideoTitle>
                  <ControlButton onClick={goFullscreen}>
                    <FaExpand />
                  </ControlButton>
                </VideoControls>
              </VideoModalContent>
            ) : (
              <ImageModalContent>
                <ModalImage src={selectedItem.image} alt={selectedItem.title} />
                <ModalInfo>
                  <ModalTitle>{selectedItem.title}</ModalTitle>
                  <ModalCategory>{selectedItem.category}</ModalCategory>
                </ModalInfo>
              </ImageModalContent>
            )}
          </ModalContent>
        </Modal>
      )}

      <GalleryFooter>
        <GetInTouchSection>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Want Something Custom Made?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Contact us today to discuss your custom jewelry or grillz design.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ContactButtonLarge
              href="/contact"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              Get In Touch
            </ContactButtonLarge>
          </motion.div>
        </GetInTouchSection>
      </GalleryFooter>
    </GalleryContainer>
  );
};

// Keyframes
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
`;

// Styled Components
const GalleryContainer = styled.div`
  background-color: var(--dark-bg);
  color: var(--white);
  min-height: 100vh;
`;

const PageHeader = styled.div`
  padding: 150px 0 80px;
  text-align: center;
  background: linear-gradient(to bottom, var(--darker-bg), var(--dark-bg));

  h1 {
    font-size: 4rem;
    text-transform: uppercase;
    font-weight: 800;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 3rem;
    }

    @media (max-width: 576px) {
      font-size: 2.5rem;
    }
  }

  .underline {
    height: 4px;
    background: var(--gold-gradient);
    margin: 0 auto;
    border-radius: 2px;
  }

  p {
    max-width: 800px;
    margin: var(--spacing-md) auto 0;
    font-size: 1.1rem;
    color: var(--silver);

    @media (max-width: 768px) {
      padding: 0 var(--spacing-md);
    }
  }
`;

const FilterContainer = styled.div`
  text-align: center;
  max-width: 1200px;
  margin: 0 auto var(--spacing-lg);
  padding: 0 var(--spacing-md);
`;

const FilterButton = styled(motion.button)`
  background: ${(props) =>
    props.active ? "var(--gold-gradient)" : "rgba(255, 255, 255, 0.05)"};
  color: ${(props) => (props.active ? "var(--black)" : "var(--white)")};
  border: ${(props) =>
    props.active ? "none" : "1px solid rgba(255, 215, 0, 0.2)"};
  border-radius: 50px;
  padding: 12px 24px;
  margin: 0 8px 10px;
  font-size: 0.9rem;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
    props.active ? "var(--gold-gradient)" : "rgba(255, 215, 0, 0.1)"};
  }

  @media (max-width: 576px) {
    padding: 10px 18px;
    font-size: 0.85rem;
    margin: 0 5px 8px;
  }
`;

const GalleryGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-md);
  max-width: 1200px;
  margin: 0 auto var(--spacing-xl);
  padding: 0 var(--spacing-md);

  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1/1;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 215, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    border-color: rgba(255, 215, 0, 0.3);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;

  ${GalleryItem}:hover & {
    transform: scale(1.1);
  }
`;

const VideoThumbnail = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--darker-bg);

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;

  svg {
    font-size: 3rem;
    color: var(--gold);
    animation: ${pulse} 2s ease-in-out infinite;
  }

  ${GalleryItem}:hover & {
    background: rgba(0, 0, 0, 0.6);

    svg {
      transform: scale(1.2);
    }
  }
`;

const GalleryItemOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
  padding: var(--spacing-md);
  transform: translateY(100%);
  transition: transform 0.3s ease;

  ${GalleryItem}:hover & {
    transform: translateY(0);
  }
`;

const GalleryItemTitle = styled.h3`
  font-size: 1.1rem;
  color: white;
  margin-bottom: 5px;
`;

const GalleryItemCategory = styled.p`
  font-size: 0.85rem;
  color: var(--gold);
  text-transform: capitalize;
`;

const NoResults = styled.div`
  text-align: center;
  padding: var(--spacing-xl) 0;

  p {
    font-size: 1.1rem;
    color: var(--silver);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
`;

const ModalContent = styled(motion.div)`
  position: relative;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  background: var(--darker-bg);
  border-radius: var(--radius-lg);
  overflow: hidden;
  z-index: 101;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 215, 0, 0.2);
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 102;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    color: var(--gold);
    background: rgba(0, 0, 0, 0.8);
  }
`;

const VideoModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: black;

  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: pointer;
  }
`;

const BigPlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: var(--gold-gradient);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.4);
  transition: all 0.3s ease;

  svg {
    font-size: 2rem;
    color: var(--black);
    margin-left: 5px;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const VideoControls = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-md);
  background: rgba(0, 0, 0, 0.8);
  gap: var(--spacing-md);
`;

const ControlButton = styled.button`
  background: transparent;
  border: 1px solid var(--gold);
  color: var(--gold);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--gold);
    color: var(--black);
  }

  svg {
    font-size: 1rem;
  }
`;

const VideoTitle = styled.h3`
  flex: 1;
  color: white;
  font-size: 1.1rem;
  text-align: center;
`;

const ImageModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalImage = styled.img`
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
`;

const ModalInfo = styled.div`
  padding: var(--spacing-md);
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 5px;
`;

const ModalCategory = styled.p`
  font-size: 1rem;
  color: var(--gold);
  text-transform: capitalize;
`;

const GalleryFooter = styled.div`
  padding: var(--spacing-xl) 0;
  background: linear-gradient(
    to right,
    rgba(255, 215, 0, 0.03),
    rgba(255, 215, 0, 0.08)
  );
`;

const GetInTouchSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  padding: 0 var(--spacing-md);

  h2 {
    font-size: 2.5rem;
    color: white;
    margin-bottom: var(--spacing-md);

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    color: var(--silver);
    margin-bottom: var(--spacing-lg);
    line-height: 1.6;
  }
`;

const ContactButtonLarge = styled(motion.a)`
  display: inline-block;
  background: var(--gold-gradient);
  color: var(--black);
  border: none;
  border-radius: 50px;
  padding: 16px 40px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
`;

export default Gallery;
